import React, { useEffect, useState } from "react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import { BACKEND_URL } from "../../config";

interface IncomeRecord {
  bill_id: number;
  visit_id?: number;
  date: string;
  patient_name: string;
  patient_phone?: string;
  income: number;
  expense: number;
  profit: number;
  paid_amount: number;
  balance_amount: number;
  status?: string;
}

interface IncomeReportResponse {
  date_from: string;
  date_to: string;
  summary: {
    income: number;
    expense: number;
    profit: number;
    paid_amount: number;
    balance_amount: number;
    bill_count: number;
    average_bill: number;
  };
  records: IncomeRecord[];
}

const today = () => new Date().toISOString().slice(0, 10);

const monthStart = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
};

const money = (value?: number) => `Rs ${Number(value || 0).toFixed(2)}`;

const emptyReport: IncomeReportResponse = {
  date_from: today(),
  date_to: today(),
  summary: {
    income: 0,
    expense: 0,
    profit: 0,
    paid_amount: 0,
    balance_amount: 0,
    bill_count: 0,
    average_bill: 0,
  },
  records: [],
};

const IncomeReport: React.FC = () => {
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [report, setReport] = useState<IncomeReportResponse>(emptyReport);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, title: "", message: "" });

  const loadReport = async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: from, date_to: to });
      const res = await fetch(`${BACKEND_URL}/api/reports/income?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      setReport(await res.json());
    } catch (err: any) {
      setModal({ open: true, title: "Unable to load report", message: err.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport(today(), today());
  }, []);

  const showToday = () => {
    const value = today();
    setDateFrom(value);
    setDateTo(value);
    loadReport(value, value);
  };

  const showCurrentMonth = () => {
    const from = monthStart();
    const to = today();
    setDateFrom(from);
    setDateTo(to);
    loadReport(from, to);
  };

  const summaryCards = [
    { label: "Income", value: money(report.summary.income), tone: "text-success-green" },
    { label: "Expense", value: money(report.summary.expense), tone: "text-alert-red" },
    { label: "Profit", value: money(report.summary.profit), tone: report.summary.profit >= 0 ? "text-success-green" : "text-alert-red" },
    { label: "Bills", value: String(report.summary.bill_count), tone: "text-primary" },
    { label: "Paid", value: money(report.summary.paid_amount), tone: "text-text-light" },
    { label: "Balance", value: money(report.summary.balance_amount), tone: "text-warning-amber" },
  ];

  return (
    <SidebarLayout title="Income Report" subtitle="Review income, expense, and profit by date range.">
      <Modal open={modal.open} title={modal.title} message={modal.message} onClose={() => setModal((prev) => ({ ...prev, open: false }))} />

      <section className="rounded border border-border-light bg-surface-light p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label>
              <div className="mb-1 text-sm font-semibold">From</div>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full rounded border p-3" />
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">To</div>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full rounded border p-3" />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={showToday} className="rounded border border-border-light px-4 py-3 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary">
              Today
            </button>
            <button type="button" onClick={showCurrentMonth} className="rounded border border-border-light px-4 py-3 font-semibold text-subtle-light hover:bg-primary/10 hover:text-primary">
              Current Month
            </button>
            <button type="button" onClick={() => loadReport()} className="rounded bg-primary px-5 py-3 font-semibold text-white">
              Apply Range
            </button>
          </div>
        </div>
      </section>

      <section className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        {summaryCards.map((card) => (
          <div key={card.label} className="rounded border border-border-light bg-surface-light p-4">
            <div className="text-sm font-semibold text-subtext-light">{card.label}</div>
            <div className={`mt-2 text-2xl font-black ${card.tone}`}>{card.value}</div>
          </div>
        ))}
      </section>

      <section className="mt-5 rounded border border-border-light bg-surface-light p-5">
        <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
          <h2 className="text-xl font-bold">Income Details</h2>
          <div className="text-sm text-subtext-light">
            {report.date_from} to {report.date_to}
          </div>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="bg-background-light text-xs uppercase text-subtext-light">
                <tr>
                  <th className="p-2">Bill</th>
                  <th className="p-2">Date</th>
                  <th className="p-2">Patient</th>
                  <th className="p-2 text-right">Income</th>
                  <th className="p-2 text-right">Expense</th>
                  <th className="p-2 text-right">Profit</th>
                  <th className="p-2 text-right">Paid</th>
                  <th className="p-2 text-right">Balance</th>
                  <th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {report.records.map((record) => (
                  <tr key={record.bill_id} className="border-t">
                    <td className="p-2 font-semibold">#{record.bill_id}</td>
                    <td className="p-2">{record.date}</td>
                    <td className="p-2">
                      <div className="font-semibold">{record.patient_name}</div>
                      <div className="text-xs text-subtext-light">{record.patient_phone || "-"}</div>
                    </td>
                    <td className="p-2 text-right">{money(record.income)}</td>
                    <td className="p-2 text-right">{money(record.expense)}</td>
                    <td className={`p-2 text-right font-bold ${record.profit >= 0 ? "text-success-green" : "text-alert-red"}`}>{money(record.profit)}</td>
                    <td className="p-2 text-right">{money(record.paid_amount)}</td>
                    <td className="p-2 text-right">{money(record.balance_amount)}</td>
                    <td className="p-2 capitalize">{record.status || "-"}</td>
                  </tr>
                ))}
                {!report.records.length && (
                  <tr>
                    <td colSpan={9} className="p-4 text-center text-subtext-light">No income records found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </SidebarLayout>
  );
};

export default IncomeReport;
